import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaYoutube,
  FaInstagram,
  FaFacebookSquare,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaPlay,
  FaEye,
  FaMagic,
  FaClock,
  FaTrash,
  FaCopy,
  FaExternalLinkAlt,
  FaUser,
  FaFileCsv,
  FaInbox,
  FaExclamationTriangle,
  FaTrophy,
  FaLayerGroup,
  FaChevronLeft,
  FaChevronRight,
  FaUserSlash,
  FaFilter,
  FaCrown,
  FaTh,
  FaList,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= platforms ================= */
// Full class names are written out so Tailwind keeps them.

const PLATFORMS = {
  "youtube-video": {
    type: "Youtube Video",
    title: "YouTube Videos",
    tab: "YT Videos",
    icon: FaYoutube,
    vertical: false,
    hero: "from-slate-900 via-slate-900 to-red-950",
    glow: "bg-red-600/40",
    glow2: "bg-rose-500/20",
    solid: "bg-red-600 hover:bg-red-500",
    solidFlat: "bg-red-600",
    selRing: "border-red-500 ring-2 ring-red-500/30",
    soft: "bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200",
    softRing: "bg-red-50 dark:bg-red-900/20 ring-1 ring-red-300",
    text: "text-red-600",
    accent: "accent-red-600",
    focus: "focus:ring-red-500",
    thumbBg: "from-red-600 to-rose-800",
  },
  "youtube-short": {
    type: "Youtube Short",
    title: "YouTube Shorts",
    tab: "YT Shorts",
    icon: FaYoutube,
    vertical: true,
    hero: "from-slate-900 via-rose-950 to-red-950",
    glow: "bg-rose-500/40",
    glow2: "bg-red-500/20",
    solid: "bg-rose-600 hover:bg-rose-500",
    solidFlat: "bg-rose-600",
    selRing: "border-rose-500 ring-2 ring-rose-500/30",
    soft: "bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200",
    softRing: "bg-rose-50 dark:bg-rose-900/20 ring-1 ring-rose-300",
    text: "text-rose-600",
    accent: "accent-rose-600",
    focus: "focus:ring-rose-500",
    thumbBg: "from-rose-500 to-red-800",
  },
  "instagram-reel": {
    type: "Instagram Reel",
    title: "Instagram Reels",
    tab: "IG Reels",
    icon: FaInstagram,
    vertical: true,
    hero: "from-slate-900 via-fuchsia-950 to-orange-950",
    glow: "bg-fuchsia-500/40",
    glow2: "bg-orange-500/25",
    solid: "bg-pink-600 hover:bg-pink-500",
    solidFlat: "bg-pink-600",
    selRing: "border-pink-500 ring-2 ring-pink-500/30",
    soft: "bg-pink-50 text-pink-800 dark:bg-pink-900/30 dark:text-pink-200",
    softRing: "bg-pink-50 dark:bg-pink-900/20 ring-1 ring-pink-300",
    text: "text-pink-600",
    accent: "accent-pink-600",
    focus: "focus:ring-pink-500",
    thumbBg: "from-fuchsia-600 via-pink-500 to-orange-400",
  },
  "facebook-video": {
    type: "Facebook Video",
    title: "Facebook Videos",
    tab: "FB Videos",
    icon: FaFacebookSquare,
    vertical: false,
    hero: "from-slate-900 via-slate-900 to-blue-950",
    glow: "bg-blue-600/40",
    glow2: "bg-sky-500/20",
    solid: "bg-blue-600 hover:bg-blue-500",
    solidFlat: "bg-blue-600",
    selRing: "border-blue-500 ring-2 ring-blue-500/30",
    soft: "bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
    softRing: "bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300",
    text: "text-blue-600",
    accent: "accent-blue-600",
    focus: "focus:ring-blue-500",
    thumbBg: "from-blue-600 to-indigo-800",
  },
  "facebook-reel": {
    type: "Facebook Reel",
    title: "Facebook Reels",
    tab: "FB Reels",
    icon: FaFacebookSquare,
    vertical: true,
    hero: "from-slate-900 via-indigo-950 to-blue-950",
    glow: "bg-indigo-500/40",
    glow2: "bg-blue-500/20",
    solid: "bg-indigo-600 hover:bg-indigo-500",
    solidFlat: "bg-indigo-600",
    selRing: "border-indigo-500 ring-2 ring-indigo-500/30",
    soft: "bg-indigo-50 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200",
    softRing: "bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-300",
    text: "text-indigo-600",
    accent: "accent-indigo-600",
    focus: "focus:ring-indigo-500",
    thumbBg: "from-indigo-600 to-blue-800",
  },
};

/* ================= styles ================= */

const CSS = `
@keyframes vlFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes vlFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes vlZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes vlBar { from { width: 0; } }
@keyframes vlGlow { 0%,100% { opacity: .55; } 50% { opacity: .9; } }
.vl-in { animation: vlFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.vl-fade { animation: vlFade .2s ease both; }
.vl-zoom { animation: vlZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.vl-bar { animation: vlBar .9s cubic-bezier(.2,.7,.2,1) both; }
.vl-glow { animation: vlGlow 4s ease-in-out infinite; }
.vl-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.vl-press:hover { transform: translateY(-1px); }
.vl-press:active { transform: scale(.96); }
.vl-card { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.vl-card:hover { transform: translateY(-4px); box-shadow: 0 20px 36px -18px rgba(15,23,42,.45); }
.vl-card .vl-thumb img { transition: transform .5s cubic-bezier(.2,.8,.2,1); }
.vl-card:hover .vl-thumb img { transform: scale(1.06); }
.vl-card .vl-hover { opacity: 0; transition: opacity .2s ease; }
.vl-card:hover .vl-hover, .vl-card:focus-within .vl-hover { opacity: 1; }
.vl-card .vl-playbtn { transform: scale(.8); opacity: 0; transition: transform .25s cubic-bezier(.2,.8,.2,1), opacity .2s ease; }
.vl-card:hover .vl-playbtn { transform: scale(1); opacity: 1; }
.vl-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .vl-in, .vl-fade, .vl-zoom, .vl-bar, .vl-glow { animation: none; }
  .vl-press:hover, .vl-press:active, .vl-card:hover, .vl-card:hover .vl-thumb img { transform: none; }
  .vl-card .vl-playbtn { transform: none; opacity: 1; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT_BASE =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2";

/* ================= helpers ================= */

const DAY = 24 * 60 * 60 * 1000;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const ago = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return `${Math.floor(months / 12)} yr ago`;
};
const fmtWatch = (s) => {
  const n = Number(s || 0);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};
const igCode = (url) => {
  const m = String(url || "").match(/instagram\.com\/(?:[^/]+\/)?(?:reel|reels|p|tv)\/([A-Za-z0-9_-]+)/i);
  return m ? m[1] : null;
};

const embedUrl = (url) => {
  const y = ytId(url);
  if (y) return `https://www.youtube-nocookie.com/embed/${y}?autoplay=1&rel=0`;
  const ig = igCode(url);
  if (ig) return `https://www.instagram.com/reel/${ig}/embed`;
  if (/facebook\.com|fb\.watch/i.test(String(url || ""))) return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=true`;
  return null;
};

// Link saved under the wrong type
const mismatchOf = (key, url) => {
  const u = String(url || "").toLowerCase();
  if (key === "youtube-video" && /youtube\.com\/shorts\//.test(u)) return "Short link";
  if (key === "facebook-video" && /\/reel\//.test(u)) return "Reel link";
  if (key === "instagram-reel" && u && !/instagram\.com/.test(u)) return "Not Instagram";
  if (key.startsWith("youtube") && u && !ytId(u)) return "Not YouTube";
  if (key.startsWith("facebook") && u && !/facebook\.com|fb\.watch/.test(u)) return "Not Facebook";
  return null;
};

const deletedUser = (v) => !v.user_name || v.user_name === "User Deleted";

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="vl-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ src, name, size = 32 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err) return <img src={src} alt="" onError={() => setErr(true)} className="rounded-full object-cover shrink-0 bg-slate-100" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </span>
  );
};

/* ================= page ================= */

const VideoList = () => {
  const { vid_type } = useParams();
  const key = PLATFORMS[vid_type] ? vid_type : "youtube-video";
  const P = PLATFORMS[key];
  const PIcon = P.icon;
  const INPUT = `${INPUT_BASE} ${P.focus}`;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [creator, setCreator] = useState(null);
  const [flag, setFlag] = useState("all");
  const [range, setRange] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [view, setView] = useState("grid");
  const [checked, setChecked] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE = 16;

  const [player, setPlayer] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/videos.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await post("videos-list", { type: P.type });
      setVideos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: `Failed to fetch ${P.title}` });
    }
    setLoading(false);
  }, [post, P.type, P.title]);

  // Reset when switching platform
  useEffect(() => {
    setVideos([]);
    setQuery("");
    setCreator(null);
    setFlag("all");
    setRange("all");
    setChecked([]);
    setPlayer(null);
    fetchVideos();
  }, [fetchVideos]);

  const runDelete = async () => {
    if (!confirm?.length) return;
    setWorking(true);
    let ok = 0;
    for (const v of confirm) {
      try {
        const res = await post("delete-video", { post_id: v.post_id });
        if (res && res.status === false) throw new Error("Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    setPlayer(null);
    if (ok) toast.current?.show({ severity: "success", summary: "Removed", detail: ok === 1 ? "Video removed successfully" : `${ok} videos removed` });
    if (ok < confirm.length) toast.current?.show({ severity: "error", summary: "Error", detail: "Some videos could not be removed" });
    fetchVideos();
  };

  /* ----- derived ----- */

  const q = query.trim();

  const creators = useMemo(() => {
    const m = {};
    videos.forEach((v) => {
      const k = v.user_id;
      if (!m[k]) m[k] = { user_id: k, name: v.user_name, profile: v.user_profile, count: 0, views: 0, karma: 0 };
      m[k].count += 1;
      m[k].views += Number(v.views_num || 0);
      m[k].karma += Number(v.point_used || 0);
    });
    return Object.values(m).sort((a, b) => b.views - a.views || b.count - a.count);
  }, [videos]);

  const stats = useMemo(
    () => ({
      total: videos.length,
      views: videos.reduce((s, v) => s + Number(v.views_num || 0), 0),
      karma: videos.reduce((s, v) => s + Number(v.point_used || 0), 0),
      creators: creators.length,
      mismatch: videos.filter((v) => mismatchOf(key, v.link_url)).length,
      deleted: videos.filter(deletedUser).length,
      zero: videos.filter((v) => !Number(v.views_num)).length,
    }),
    [videos, creators, key]
  );

  const maxViews = useMemo(() => Math.max(1, ...videos.map((v) => Number(v.views_num || 0))), [videos]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const now = Date.now();
    let l = videos.filter((v) => {
      if (creator && String(v.user_id) !== String(creator)) return false;
      if (flag === "mismatch" && !mismatchOf(key, v.link_url)) return false;
      if (flag === "deleted" && !deletedUser(v)) return false;
      if (flag === "zero" && Number(v.views_num)) return false;
      if (range !== "all") {
        const d = parseAny(v.link_date);
        if (!d) return false;
        if (range === "7d" && now - d.getTime() > 7 * DAY) return false;
        if (range === "30d" && now - d.getTime() > 30 * DAY) return false;
      }
      if (!ql) return true;
      return [v.link_url, v.post_id, v.user_name, v.user_id].map((x) => String(x ?? "").toLowerCase()).some((x) => x.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "views") return Number(b.views_num || 0) - Number(a.views_num || 0);
      if (sortBy === "least") return Number(a.views_num || 0) - Number(b.views_num || 0);
      if (sortBy === "karma") return Number(b.point_used || 0) - Number(a.point_used || 0);
      if (sortBy === "name") return String(a.user_name || "").localeCompare(String(b.user_name || ""));
      const da = parseAny(a.link_date)?.getTime() || 0;
      const db = parseAny(b.link_date)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [videos, q, creator, flag, range, sortBy, key]);

  useEffect(() => setPage(1), [q, creator, flag, range, sortBy, view]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);
  const allPageChecked = pageRows.length > 0 && pageRows.every((v) => checked.includes(v.post_id));
  const checkedVideos = videos.filter((v) => checked.includes(v.post_id));
  const creatorInfo = creators.find((c) => String(c.user_id) === String(creator));

  const hasFilters = q || creator || flag !== "all" || range !== "all";
  const clearFilters = () => {
    setQuery("");
    setCreator(null);
    setFlag("all");
    setRange("all");
  };
  const toggleCheck = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = (list) => {
    const head = ["Video Id", "Upload By", "User Id", "Video Link", "Watch Time", "Karma Used", "Views", "Date Time"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = list.map((v) => [v.post_id, v.user_name, v.user_id, v.link_url, v.watch_time, v.point_used, v.views_num, v.link_date].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-${key}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (player !== null) {
        if (e.key === "Escape") setPlayer(null);
        if (e.key === "ArrowRight") setPlayer((i) => Math.min(filtered.length - 1, i + 1));
        if (e.key === "ArrowLeft") setPlayer((i) => Math.max(0, i - 1));
        return;
      }
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, player, filtered.length]);

  const current = player !== null ? filtered[player] : null;

  /* ----- thumbnail (function, not component, so images don't reload) ----- */

  const thumb = (v) => {
    const id = ytId(v.link_url);
    const vertical = P.vertical || /youtube\.com\/shorts\//i.test(v.link_url || "");
    return (
      <div className="vl-thumb relative overflow-hidden bg-slate-900" style={{ aspectRatio: "16/9" }}>
        {id ? (
          <>
            {vertical && <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60" />}
            <img
              src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              className={vertical ? "absolute inset-y-0 left-1/2 -translate-x-1/2 h-full object-cover" : "absolute inset-0 w-full h-full object-cover"}
              style={vertical ? { aspectRatio: "9/16" } : {}}
            />
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${P.thumbBg} flex flex-col items-center justify-center text-white`}>
            <PIcon size={34} className="opacity-90" />
            <span className="mt-1.5 text-[11px] font-mono opacity-80 max-w-[85%] truncate">{igCode(v.link_url) || String(v.link_url || "").replace(/^https?:\/\/(www\.)?/, "").slice(0, 34)}</span>
          </div>
        )}
      </div>
    );
  };

  const flags = [
    { id: "all", label: "Everything" },
    { id: "mismatch", label: `Wrong link type (${stats.mismatch})`, warn: stats.mismatch > 0 },
    { id: "deleted", label: `Uploader deleted (${stats.deleted})`, warn: stats.deleted > 0 },
    { id: "zero", label: `No views yet (${stats.zero})` },
  ];

  const statCards = [
    { label: P.title, value: stats.total, icon: FaLayerGroup },
    { label: "Total views", value: stats.views, icon: FaEye },
    { label: "Karma spent", value: stats.karma, icon: FaMagic },
    { label: "Creators", value: stats.creators, icon: FaUser },
  ];

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Hero */}
      <div key={key} className={`vl-in relative overflow-hidden rounded-3xl bg-gradient-to-br ${P.hero} text-white p-6 sm:p-8`}>
        <div className={`vl-glow pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full ${P.glow} blur-3xl`} />
        <div className={`vl-glow pointer-events-none absolute -bottom-28 left-1/3 w-72 h-72 rounded-full ${P.glow2} blur-3xl`} style={{ animationDelay: "2s" }} />

        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-sm text-white/60">Videos</p>
            <h1 className="mt-1 flex items-center gap-3 text-3xl font-bold tracking-tight">
              <span className={`w-11 h-11 rounded-xl ${P.solidFlat} flex items-center justify-center shadow-lg`}>
                <PIcon size={22} />
              </span>
              {P.title}
            </h1>
            <p className="mt-2 text-sm text-white/70 max-w-md">Creator uploads for {P.type}. Watch, check and remove from one place.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => exportCsv(filtered)} disabled={!filtered.length} className="vl-press inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium disabled:opacity-40">
              <FaFileCsv /> Export
            </button>
            <button type="button" onClick={fetchVideos} disabled={loading} className={`vl-press inline-flex items-center gap-2 h-10 px-4 rounded-xl ${P.solid} text-sm font-semibold shadow-lg disabled:opacity-60`}>
              <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Platform switcher */}
        <div className="relative mt-6 flex gap-1 p-1 rounded-2xl bg-white/10 backdrop-blur overflow-x-auto w-fit max-w-full">
          {Object.entries(PLATFORMS).map(([k, p]) => {
            const Icon = p.icon;
            const active = k === key;
            return (
              <button
                key={k}
                type="button"
                onClick={() => navigate(`/videos/${k}`)}
                className={`shrink-0 inline-flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-semibold transition-all active:scale-95 ${active ? "bg-white text-slate-900 shadow" : "text-white/75 hover:text-white hover:bg-white/10"}`}
              >
                <Icon size={13} className={active ? p.text : ""} /> {p.tab}
              </button>
            );
          })}
        </div>

        {/* Stats */}
        <div className="relative mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="vl-in rounded-2xl bg-white/[.06] border border-white/10 p-4" style={{ animationDelay: `${100 + i * 70}ms` }}>
                <span className={`w-9 h-9 rounded-lg ${P.solidFlat} flex items-center justify-center shadow`}>
                  <Icon size={14} />
                </span>
                <p className="mt-3 text-2xl font-bold tabular-nums">{loading && !videos.length ? "–" : fmt(c.value)}</p>
                <p className="text-xs text-white/60">{c.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mt-4">
        <div className="xl:col-span-3 min-w-0">
          {/* Search + filters */}
          <div className={`${CARD} vl-in p-4`} style={{ animationDelay: "200ms" }}>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by link, video ID, creator name or user ID" className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {query ? (
                    <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                      <FaTimes size={13} />
                    </button>
                  ) : (
                    <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
                  )}
                </div>
              </div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-12 px-3 md:w-44`}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="views">Most views</option>
                <option value="least">Least views</option>
                <option value="karma">Most karma</option>
                <option value="name">Uploader A–Z</option>
              </select>
              <select value={range} onChange={(e) => setRange(e.target.value)} className={`${INPUT} h-12 px-3 md:w-40`}>
                <option value="all">Any date</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <FaFilter className="text-slate-400" size={11} />
              {flags.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFlag(f.id)}
                  className={`vl-press h-8 px-3 rounded-full text-xs font-semibold border ${
                    flag === f.id
                      ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900"
                      : f.warn
                      ? "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800"
                      : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              {creatorInfo && (
                <span className={`inline-flex items-center gap-2 h-8 pl-1 pr-2 rounded-full text-xs font-semibold ${P.soft}`}>
                  <Avatar src={creatorInfo.profile} name={creatorInfo.name} size={24} />
                  {creatorInfo.name}
                  <button type="button" onClick={() => setCreator(null)} aria-label="Remove creator filter">
                    <FaTimes size={10} />
                  </button>
                </span>
              )}
              {hasFilters && (
                <button type="button" onClick={clearFilters} className={`ml-auto text-sm font-medium ${P.text} hover:underline`}>
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Result bar */}
          <div className="flex flex-wrap items-center gap-3 mt-4 mb-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={allPageChecked}
                onChange={() => setChecked((c) => (allPageChecked ? c.filter((id) => !pageRows.some((v) => v.post_id === id)) : Array.from(new Set([...c, ...pageRows.map((v) => v.post_id)]))))}
                className={`w-4 h-4 ${P.accent}`}
              />
              Select page
            </label>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {q ? (
                <>
                  <b>{filtered.length}</b> result{filtered.length === 1 ? "" : "s"} for "<b>{q}</b>"
                </>
              ) : (
                <>
                  <b>{filtered.length}</b> upload{filtered.length === 1 ? "" : "s"}
                </>
              )}
            </p>
            {checked.length > 0 && (
              <div className="vl-fade flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{checked.length} selected</span>
                <button type="button" onClick={() => setConfirm(checkedVideos)} className={`vl-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl ${P.solid} text-white text-sm font-semibold`}>
                  <FaTrash size={11} /> Remove
                </button>
                <button type="button" onClick={() => exportCsv(checkedVideos)} className="vl-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  <FaFileCsv size={11} /> Export
                </button>
                <button type="button" onClick={() => setChecked([])} className="text-sm text-slate-500 hover:underline">
                  Clear
                </button>
              </div>
            )}
            <div className="ml-auto inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
              {[
                { id: "grid", icon: FaTh, label: "Grid view" },
                { id: "list", icon: FaList, label: "List view" },
              ].map((vv) => {
                const Icon = vv.icon;
                return (
                  <button key={vv.id} type="button" onClick={() => setView(vv.id)} aria-label={vv.label} title={vv.label} className={`w-8 h-7 rounded-md flex items-center justify-center transition-all active:scale-95 ${view === vv.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                    <Icon size={12} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          {loading && !videos.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`${CARD} overflow-hidden`}>
                  <div className="bg-slate-200 dark:bg-slate-700 animate-pulse" style={{ aspectRatio: "16/9" }} />
                  <div className="p-3 space-y-2">
                    <div className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                    <div className="h-3 w-2/3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : pageRows.length === 0 ? (
            <div className={`${CARD} px-5 py-16 text-center`}>
              <FaInbox className="mx-auto text-slate-300" size={36} />
              <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{q ? `No video found for "${q}"` : `No ${P.title} yet`}</p>
              {hasFilters && (
                <button type="button" onClick={clearFilters} className={`mt-2 text-sm font-medium ${P.text} hover:underline`}>
                  Clear all filters
                </button>
              )}
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
              {pageRows.map((v, i) => {
                const d = parseAny(v.link_date);
                const sel = checked.includes(v.post_id);
                const mm = mismatchOf(key, v.link_url);
                const views = Number(v.views_num || 0);
                const idx = (safePage - 1) * PAGE + i;
                return (
                  <article key={v.post_id} className={`vl-card vl-in rounded-2xl bg-white dark:bg-secondary-dark-bg border overflow-hidden ${sel ? P.selRing : "border-slate-200 dark:border-slate-700"}`} style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}>
                    <div className="relative">
                      <button type="button" onClick={() => setPlayer(idx)} className="block w-full text-left" aria-label="Play video">
                        {thumb(v)}
                        <span className={`vl-playbtn absolute inset-0 m-auto w-14 h-14 rounded-full ${P.solidFlat} text-white flex items-center justify-center shadow-2xl`}>
                          <FaPlay className="ml-1" />
                        </span>
                      </button>
                      <label className={`absolute top-2 left-2 w-7 h-7 rounded-lg bg-white/95 dark:bg-slate-900/80 flex items-center justify-center cursor-pointer shadow ${sel ? "" : "vl-hover"}`}>
                        <input type="checkbox" checked={sel} onChange={() => toggleCheck(v.post_id)} className={`w-4 h-4 ${P.accent}`} aria-label={`Select ${v.post_id}`} />
                      </label>
                      {mm && (
                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-400 text-amber-950" title="This link does not match the upload type">
                          <FaExclamationTriangle size={8} /> {mm}
                        </span>
                      )}
                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-black/80 text-white tabular-nums">
                        <FaClock size={9} /> {fmtWatch(v.watch_time)}
                      </span>
                      <div className="absolute bottom-0 inset-x-0 h-1 bg-black/30">
                        <div className={`vl-bar h-full ${P.solidFlat}`} style={{ width: `${(views / maxViews) * 100}%` }} />
                      </div>
                    </div>

                    <div className="p-3 flex gap-3">
                      <button type="button" onClick={() => setCreator(v.user_id)} title="Show only this creator" className="vl-press shrink-0">
                        <Avatar src={v.user_profile} name={v.user_name} size={36} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <button type="button" onClick={() => setCreator(v.user_id)} className={`block text-sm font-semibold truncate hover:underline text-left ${deletedUser(v) ? "text-rose-500 italic" : "text-slate-900 dark:text-white"}`}>
                          <Hi text={v.user_name || "User Deleted"} q={q} />
                        </button>
                        <p className="text-xs text-slate-500 truncate" title={v.link_url}>
                          <Hi text={String(v.link_url || "").replace(/^https?:\/\/(www\.)?/, "")} q={q} />
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {compact(views)} views <span className="mx-1">•</span> {ago(d)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 px-3 pb-3">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                        <FaMagic size={9} /> {fmt(v.point_used)} karma
                      </span>
                      <div className="ml-auto flex gap-1">
                        <button type="button" onClick={() => copyText(v.link_url, "Link copied")} className="vl-press w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center" title="Copy link" aria-label="Copy link">
                          <FaCopy size={11} />
                        </button>
                        <a href={v.link_url} target="_blank" rel="noreferrer" className="vl-press w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center" title="Open original" aria-label="Open original">
                          <FaExternalLinkAlt size={11} />
                        </a>
                        <button type="button" onClick={() => setConfirm([v])} className="vl-press w-8 h-8 rounded-lg text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 flex items-center justify-center" title="Remove" aria-label="Remove">
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={`${CARD} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="pl-4 py-3 w-8" />
                      <th className="px-3 py-3 font-medium text-left">Video</th>
                      <th className="px-3 py-3 font-medium text-left">Upload By</th>
                      <th className="px-3 py-3 font-medium text-right whitespace-nowrap">Karma Used</th>
                      <th className="px-3 py-3 font-medium text-right">Views</th>
                      <th className="px-3 py-3 font-medium text-left whitespace-nowrap">Date Time</th>
                      <th className="px-3 py-3 pr-4 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {pageRows.map((v, i) => {
                      const d = parseAny(v.link_date);
                      const mm = mismatchOf(key, v.link_url);
                      const idx = (safePage - 1) * PAGE + i;
                      return (
                        <tr key={v.post_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                          <td className="pl-4 py-3">
                            <input type="checkbox" checked={checked.includes(v.post_id)} onChange={() => toggleCheck(v.post_id)} className={`w-4 h-4 ${P.accent}`} aria-label={`Select ${v.post_id}`} />
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-3 min-w-[280px]">
                              <button type="button" onClick={() => setPlayer(idx)} className="vl-card shrink-0 w-28 rounded-lg overflow-hidden" aria-label="Play video">
                                {thumb(v)}
                              </button>
                              <div className="min-w-0">
                                <a href={v.link_url} target="_blank" rel="noreferrer" className="block max-w-[240px] truncate text-slate-800 dark:text-slate-100 hover:underline" title={v.link_url}>
                                  <Hi text={v.link_url} q={q} />
                                </a>
                                <p className="text-xs text-slate-500 tabular-nums">
                                  Video <Hi text={v.post_id} q={q} />
                                </p>
                                {mm && (
                                  <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                    <FaExclamationTriangle size={8} /> {mm}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <button type="button" onClick={() => setCreator(v.user_id)} className="flex items-center gap-2 text-left min-w-[160px]">
                              <Avatar src={v.user_profile} name={v.user_name} size={30} />
                              <span className="min-w-0">
                                <span className={`block truncate font-medium ${deletedUser(v) ? "text-rose-500 italic" : "text-slate-900 dark:text-white"}`}>
                                  <Hi text={v.user_name || "User Deleted"} q={q} />
                                </span>
                                <span className="block text-xs text-slate-500 tabular-nums">
                                  <Hi text={v.user_id} q={q} />
                                </span>
                              </span>
                            </button>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums">{fmt(v.point_used)}</td>
                          <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{fmt(v.views_num)}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                            <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                          </td>
                          <td className="px-3 py-3 pr-4">
                            <div className="flex justify-end gap-1.5">
                              <button type="button" onClick={() => copyText(v.link_url, "Link copied")} className="vl-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300" aria-label="Copy link">
                                <FaCopy size={11} />
                              </button>
                              <button type="button" onClick={() => setConfirm([v])} className="vl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white dark:bg-rose-900/30 dark:text-rose-300 text-sm font-medium">
                                <FaTrash size={10} /> Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {filtered.length > PAGE && (
            <div className="flex items-center justify-center gap-3 mt-6 text-sm">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="vl-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
                <FaChevronLeft size={11} />
              </button>
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                Page <b>{safePage}</b> of {totalPages}
              </span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="vl-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
                <FaChevronRight size={11} />
              </button>
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <aside className={`${CARD} vl-in p-4 xl:sticky xl:top-4 self-start`} style={{ animationDelay: "260ms" }}>
          <div className="flex items-center gap-2">
            <FaTrophy className="text-amber-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Top creators</h2>
          </div>
          <p className="text-xs text-slate-500">By total views. Click to see only their uploads.</p>
          <ul className="mt-3 space-y-1 max-h-[60vh] overflow-y-auto">
            {creators.slice(0, 15).map((c, i) => {
              const active = String(creator) === String(c.user_id);
              return (
                <li key={c.user_id}>
                  <button type="button" onClick={() => setCreator(active ? null : c.user_id)} className={`vl-press w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left ${active ? P.softRing : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}>
                    <span className={`w-6 text-center text-xs font-bold ${i === 0 ? "text-amber-500" : "text-slate-400"}`}>{i === 0 ? <FaCrown className="mx-auto" /> : i + 1}</span>
                    <Avatar src={c.profile} name={c.name} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${!c.name || c.name === "User Deleted" ? "text-rose-500 italic" : "text-slate-900 dark:text-white"}`}>{c.name || "User Deleted"}</p>
                      <p className="text-[11px] text-slate-500">
                        {c.count} upload{c.count > 1 ? "s" : ""} • {compact(c.views)} views
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
            {!creators.length && <li className="text-sm text-slate-500 py-4 text-center">No creators yet</li>}
          </ul>
          {(stats.mismatch > 0 || stats.deleted > 0) && (
            <div className="mt-4 space-y-2">
              {stats.mismatch > 0 && (
                <button type="button" onClick={() => setFlag("mismatch")} className="vl-press w-full flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3 text-left text-xs text-amber-900 dark:text-amber-200">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>
                    <b>{stats.mismatch}</b> upload{stats.mismatch > 1 ? "s have" : " has"} a link that doesn't match {P.type}. Tap to review.
                  </span>
                </button>
              )}
              {stats.deleted > 0 && (
                <button type="button" onClick={() => setFlag("deleted")} className="vl-press w-full flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 p-3 text-left text-xs text-rose-900 dark:text-rose-200">
                  <FaUserSlash className="mt-0.5 shrink-0" />
                  <span>
                    <b>{stats.deleted}</b> upload{stats.deleted > 1 ? "s belong" : " belongs"} to deleted users. Tap to review.
                  </span>
                </button>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* ================= Player ================= */}
      {current && (
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6" style={{ zIndex: 1100 }}>
          <div className="vl-fade absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setPlayer(null)} />
          <div className="vl-zoom relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-3 rounded-2xl overflow-hidden bg-slate-900 text-white shadow-2xl max-h-[92vh]">
            <div className="lg:col-span-2 bg-black flex items-center justify-center min-h-[260px]">
              {embedUrl(current.link_url) ? (
                <div className="w-full h-full flex items-center justify-center" style={{ maxHeight: "92vh" }}>
                  <iframe
                    key={current.post_id}
                    title="Video player"
                    src={embedUrl(current.link_url)}
                    allow="autoplay; encrypted-media; picture-in-picture; clipboard-write"
                    allowFullScreen
                    className="w-full"
                    style={{ aspectRatio: P.vertical || /shorts\//.test(current.link_url) ? "9/16" : "16/9", maxHeight: "88vh" }}
                  />
                </div>
              ) : (
                <div className="p-10 text-center text-white/70">
                  <PIcon size={40} className="mx-auto mb-2" />
                  This link can't be played here.
                  <a href={current.link_url} target="_blank" rel="noreferrer" className="block mt-2 underline">
                    Open the original
                  </a>
                </div>
              )}
            </div>

            <div className="flex flex-col min-h-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <span className="text-xs text-white/60 tabular-nums">
                  {player + 1} of {filtered.length}
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setPlayer((i) => Math.max(0, i - 1))} disabled={player <= 0} className="vl-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30" aria-label="Previous video">
                    <FaChevronLeft size={11} />
                  </button>
                  <button type="button" onClick={() => setPlayer((i) => Math.min(filtered.length - 1, i + 1))} disabled={player >= filtered.length - 1} className="vl-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30" aria-label="Next video">
                    <FaChevronRight size={11} />
                  </button>
                  <button type="button" onClick={() => setPlayer(null)} className="vl-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Close">
                    <FaTimes size={12} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar src={current.user_profile} name={current.user_name} size={44} />
                  <div className="min-w-0">
                    <p className={`font-semibold truncate ${deletedUser(current) ? "text-rose-400 italic" : ""}`}>{current.user_name || "User Deleted"}</p>
                    <button type="button" onClick={() => copyText(current.user_id, "User ID copied")} className="text-xs text-white/60 hover:text-white tabular-nums inline-flex items-center gap-1">
                      {current.user_id} <FaCopy size={9} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: FaEye, label: "Views", value: fmt(current.views_num) },
                    { icon: FaMagic, label: "Karma used", value: fmt(current.point_used) },
                    { icon: FaClock, label: "Watch time", value: `${Number(current.watch_time || 0)}s` },
                    { icon: PIcon, label: "Type", value: current.link_type },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label} className="rounded-xl bg-white/[.06] p-3">
                        <p className="flex items-center gap-1.5 text-[11px] text-white/50">
                          <Icon size={10} /> {s.label}
                        </p>
                        <p className="text-sm font-semibold mt-0.5">{s.value}</p>
                      </div>
                    );
                  })}
                </div>
                {mismatchOf(key, current.link_url) && (
                  <p className="flex items-start gap-2 rounded-xl bg-amber-400/15 text-amber-200 text-xs p-3">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" /> This link does not match {P.type} ({mismatchOf(key, current.link_url)}).
                  </p>
                )}
                <div className="rounded-xl bg-white/[.06] p-3 text-xs space-y-1.5">
                  <p className="flex justify-between gap-2">
                    <span className="text-white/50">Video Id</span>
                    <span className="tabular-nums">{current.post_id}</span>
                  </p>
                  <p className="flex justify-between gap-2">
                    <span className="text-white/50">Uploaded</span>
                    <span>
                      {fmtDate(parseAny(current.link_date))}, {fmtTime(parseAny(current.link_date))}
                    </span>
                  </p>
                  <p className="text-white/50 pt-1">Video Link</p>
                  <button type="button" onClick={() => copyText(current.link_url, "Link copied")} className="w-full text-left break-all text-white/90 hover:text-white">
                    {current.link_url} <FaCopy size={9} className="inline ml-1" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 p-4 border-t border-white/10">
                <a href={current.link_url} target="_blank" rel="noreferrer" className="vl-press h-10 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <PIcon /> Open
                </a>
                <button type="button" onClick={() => navigate(`/user-profile/${current.user_id}`)} disabled={deletedUser(current)} className="vl-press h-10 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-30">
                  <FaUser size={10} /> Profile
                </button>
                <button type="button" onClick={() => setConfirm([current])} className="vl-press h-10 rounded-xl bg-rose-600 hover:bg-rose-500 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaTrash size={10} /> Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="vl-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="vl-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 flex items-center justify-center">
              <FaTrash size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{confirm.length === 1 ? "Remove this video?" : `Remove ${confirm.length} videos?`}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">It will be removed from the app for everyone. Karma already spent on it is not returned. This cannot be undone.</p>
            <div className="mt-4 max-h-48 overflow-y-auto space-y-2">
              {confirm.slice(0, 6).map((v) => (
                <div key={v.post_id} className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2">
                  <div className="w-24 shrink-0 rounded-lg overflow-hidden">{thumb(v)}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{v.user_name || "User Deleted"}</p>
                    <p className="text-xs text-slate-500">
                      {fmt(v.views_num)} views • {fmt(v.point_used)} karma
                    </p>
                  </div>
                </div>
              ))}
              {confirm.length > 6 && <p className="text-xs text-slate-500">and {confirm.length - 6} more</p>}
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="vl-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button type="button" onClick={runDelete} disabled={working} className="vl-press flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Removing..." : "Yes, remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoList;